import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { AuthConfig, AUTH_CONFIG } from '../features/auth';
import { PagedMerchantTransactionResult, TransactionFilter } from '../models/merchant-transaction.model';
import { PagedResultDto } from '../models/paged-result.dto';
import { WalletFilterParams } from '../models/wallet.model';
import { PagedResult } from '@olv/ui-shared/models/account.interface';

export interface TransactionExportRequest {
  exportFormat: number;
  filters: {
    maxResultCount?: number;
    skipCount?: number;
    sorting?: string;
    filter?: string;
    transactionStatus?: number[];
    actionRequired?: boolean[];
    riskLevel?: number[];
    blockChain?: number[];
    transactionType?: number[];
    startDate?: string;
    endDate?: string;
    merchantIds?: string[];
  };
  emails: string[] | undefined;
}
export interface WalletExportRequest {
  exportFormat: ExportFormatEnum;
  filters: {
    maxResultCount?: number;
    skipCount?: number;
    sorting?: string;
    filter?: string;
    networkTypes?: number[];
    walletBalance?: number[];
    walletStatuses?: number[];
    walletTypes?: number[];
    systemWalletTypes?: number[];
    riskLevels?: number[];
    startDate?: string;
    endDate?: string;
    merchantIds?: string[];
  };
  emails?: string[];
}
export interface WalletReport {
  id: string;
  walletType: string;
  systemWalletType: string;
  merchantName: string | null;
  address: string;
  blockchain: string;
  currencyAmount: number;
  currencyDisplayPrecision: number;
  baseCurrency: string;
  baseCurrencySymbol: string;
  baseCurrencyDisplayPrecision: number;
  baseCurrencyAmount: number | null;
  riskLevel: string;
  status: string;
  lastModified: string;
}

export interface UserActivity {
  id: string;
  activityType: string;
  entityNameValue: string;
  keycloakUserName: string;
  date: string;
  failedLoginInfo?: FailedLoginInfo;
}

export interface FailedLoginInfo {
  ipAddress: string;
  errorReason: string;
  clientId: string;
}

export interface UserActivityParams {
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
  filter?: string;
  activityType?: number[];
  startDate?: string;
  endDate?: string;
}
export interface LogsExportRequest {
  exportFormat: ExportFormatEnum;
  filters: {
    maxResultCount?: number;
    skipCount?: number;
    sorting?: string;
    filter?: string;
    activityType?: number[];
    startDate?: string;
    endDate?: string;
  };
  emails?: string[];
}

export enum ExportFormatEnum {
  xlsx = 0,
  pdf = 1,
  csv = 2,
}

export interface LedgerExportRequest {
  merchantId: string;
  currencies: number[];
  startDate: string;
  endDate: string;
}

export interface AssetsExportRequest {
  merchantId: string;
  startDate: string;
  endDate: string;
}

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  private http = inject(HttpClient);

  private readonly baseUrl = '/api/Report';
  private readonly cfg = inject<AuthConfig>(AUTH_CONFIG);

  private apiUrl = `${this.cfg.baseUrl}/api/report`;

  /**
   * Get transaction history for a specific merchant with pagination
   * @param filter Optional filter parameters
   */
  getReportTransactions(filter?: TransactionFilter): Observable<PagedMerchantTransactionResult> {
    const url = `${this.apiUrl}/transactions`;
    let params = new HttpParams();

    if (filter) {
      if (filter.skipCount !== undefined) {
        params = params.set('SkipCount', filter.skipCount.toString());
      }

      if (filter.maxResultCount !== undefined) {
        params = params.set('MaxResultCount', filter.maxResultCount.toString());
      }

      if (filter.filter) {
        params = params.set('Filter', filter.filter);
      }

      if (filter.sorting) {
        params = params.set('Sorting', filter.sorting);
      }

      if (filter.account) {
        params = params.set('Account', filter.account);
      }

      if (filter.actionRequired !== undefined) {
        filter.actionRequired.forEach((action) => (params = params.append('ActionRequired', action.toString())));
      }

      if (filter.transactionType?.length) {
        filter.transactionType.forEach((type) => (params = params.append('TransactionType', type.toString())));
      }

      if (filter.status?.length) {
        filter.status.forEach((status) => (params = params.append('TransactionStatus', status.toString())));
      }

      if (filter.blockchain?.length) {
        filter.blockchain.forEach((network) => (params = params.append('Blockchain', network.toString())));
      }

      if (filter.riskLevel?.length) {
        filter.riskLevel.forEach((level) => (params = params.append('RiskLevel', level.toString())));
      }

      if (filter.exchangeCategories?.length) {
        filter.exchangeCategories.forEach((category) => (params = params.append('ExchangeCategories', category)));
      }

      // Tri-state: undefined applies no restriction, so false has to be sent explicitly rather than skipped.
      if (filter.exchangeRateOverridden !== undefined) {
        params = params.set('ExchangeRateOverridden', filter.exchangeRateOverridden.toString());
      }

      if (filter.startDate && filter.endDate) {
        params = params.set('StartDate', filter.startDate);
        params = params.set('EndDate', filter.endDate);
      }

      if (filter.merchantIds?.length) {
        filter.merchantIds.forEach((id) => (params = params.append('MerchantIds', id.toString())));
      }
    }

    return this.http.get<PagedMerchantTransactionResult>(url, { params }).pipe(
      map((response) => {
        const transformedItems = response.items.map((item) => ({
          ...item,
          date: new Date(item.date),
        }));

        return {
          ...response,
          items: transformedItems,
        };
      }),
      catchError(
        this.handleError<PagedMerchantTransactionResult>('getMerchantTransactions', {
          totalCount: 0,
          items: [],
        }),
      ),
    );
  }

  /**
   * Load available ledger export currencies
   */
  getLedgerCurrencies(): Observable<{ id: number; name: string }[]> {
    const url = `${this.apiUrl}/metadata/ledger-currencies`;
    return this.http.get<any[]>(url).pipe(
      map((items) =>
        (items ?? []).map((item: any) => ({
          id: (item?.id ?? item?.Id) as number,
          name: (item?.name ?? item?.Name) as string,
        })),
      ),
      catchError(this.handleError<{ id: number; name: string }[]>('getLedgerCurrencies', [])),
    );
  }

  /**
   * Load merchant filters
   */
  getMerchantFilter(): Observable<{ id: string; name: string }[]> {
    const url = `${this.apiUrl}/metadata/merchants`;
    return this.http
      .get<any[]>(url)
      .pipe(catchError(this.handleError<{ id: string; name: string }[]>('getMerchantFilter', [])));
  }

  /**
   * Load assets merchants
   */
  getAssetsMerchants(): Observable<{ id: string; name: string }[]> {
    const url = `${this.apiUrl}/metadata/assets-merchants`;
    return this.http
      .get<any[]>(url)
      .pipe(catchError(this.handleError<{ id: string; name: string }[]>('getAssetsMerchants', [])));
  }

  /**
   * Export transaction history
   * @param payload
   */
  exportTransactions(payload: TransactionExportRequest): Observable<{ blob: Blob; filename: string } | null> {
    const url = `${this.apiUrl}/transactions/export`;
    return this.http
      .post(url, payload, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          const blob = res.body!;
          const now = new Date();
          const date = now.toISOString().slice(0, 10);
          const time = now.toTimeString().slice(0, 8);

          const filename = `Transactions-${date}-${time}.${this.getExtensionFromFormat(payload.exportFormat)}`;

          return { blob, filename };
        }),
        catchError((error) => {
          console.error('Export failed:', error);
          return of(null);
        }),
      );
  }

  /**
   * Export ledger report (always xlsx)
   */
  exportLedger(payload: LedgerExportRequest): Observable<{ blob: Blob; filename: string } | null> {
    const url = `${this.apiUrl}/ledger/export`;
    return this.http
      .post(url, payload, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          const blob = res.body!;

          // Try to extract filename from Content-Disposition, otherwise build a default
          const disposition = res.headers.get('content-disposition') || '';
          let filename = '';
          const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
          if (match) {
            filename = decodeURIComponent(match[1] || match[2]);
          }
          if (!filename) {
            const now = new Date();
            const date = now.toISOString().slice(0, 10);
            const time = now.toTimeString().slice(0, 8);
            filename = `Ledger-${date}-${time}.xlsx`;
          }

          return { blob, filename };
        }),
        catchError((error) => {
          console.error('Ledger export failed:', error);
          return of(null);
        }),
      );
  }

  /**
   * Export assets report (always xlsx)
   */
  exportAssets(payload: AssetsExportRequest): Observable<{ blob: Blob; filename: string } | null> {
    const url = `${this.apiUrl}/assets/export`;
    return this.http
      .post(url, payload, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          const blob = res.body!;

          const disposition = res.headers.get('content-disposition') || '';
          let filename = '';
          const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
          if (match) {
            filename = decodeURIComponent(match[1] || match[2]);
          }
          if (!filename) {
            const now = new Date();
            const date = now.toISOString().slice(0, 10);
            const time = now.toTimeString().slice(0, 8);
            filename = `Assets-${date}-${time}.xlsx`;
          }

          return { blob, filename };
        }),
        catchError((error) => {
          console.error('Assets export failed:', error);
          return of(null);
        }),
      );
  }

  /**
   * Get report wallets with optional paging and sorting
   * @param params Paging and filtering parameters
   * @returns Observable of paged flat wallet report results
   */
  getReportWallets(params: WalletFilterParams = { skipCount: 0 }): Observable<PagedResultDto<WalletReport>> {
    let httpParams = new HttpParams();
    const url = `${this.apiUrl}/wallets`;

    const paramMap: Record<string, string> = {
      walletType: 'WalletTypes',
      status: 'WalletStatuses',
      blockchain: 'NetworkTypes',
      riskLevel: 'RiskLevels',
      balance: 'WalletBalance',
      skipCount: 'SkipCount',
      maxResultCount: 'MaxResultCount',
      filter: 'Filter',
      sorting: 'Sorting',
      startDate: 'StartDate',
      endDate: 'EndDate',
    };

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        const mappedKey = paramMap[key] || key;

        if (Array.isArray(value)) {
          value.forEach((val) => {
            httpParams = httpParams.append(mappedKey, String(val));
          });
        } else {
          httpParams = httpParams.set(mappedKey, String(value));
        }
      }
    });

    return this.http.get<PagedResultDto<WalletReport>>(url, { params: httpParams }).pipe(
      catchError((error) => {
        console.error('WalletService: Error fetching wallet report:', error);
        return of({
          totalCount: 0,
          items: [],
        });
      }),
    );
  }

  /**
   * Export wallet history
   * @param payload
   */
  exportWallets(payload: WalletExportRequest): Observable<{ blob: Blob; filename: string } | null> {
    const url = `${this.apiUrl}/wallets/export`;

    return this.http
      .post(url, payload, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          const blob = res.body!;
          // Format current date and time
          const now = new Date();
          const date = now.toISOString().slice(0, 10);
          const time = now.toTimeString().slice(0, 8);

          const filename = `Wallets-${date}-${time}.${this.getExtensionFromFormat(payload.exportFormat)}`;

          return { blob, filename };
        }),
        catchError((error) => {
          console.error('Export failed:', error);
          return of(null);
        }),
      );
  }
  /**
   * Get report user activity with optional paging and sorting
   * @param params Paging and filtering parameters
   * @returns Observable of paged flat user activity report results
   */
  getUserActivityLogs(params: UserActivityParams = {}): Observable<PagedResultDto<UserActivity>> {
    const url = `${this.apiUrl}/logs`;
    let httpParams = new HttpParams();

    if (params.skipCount !== undefined) {
      httpParams = httpParams.set('SkipCount', params.skipCount.toString());
    }

    if (params.maxResultCount !== undefined) {
      httpParams = httpParams.set('MaxResultCount', params.maxResultCount.toString());
    }

    if (params.sorting) {
      httpParams = httpParams.set('Sorting', params.sorting);
    }

    if (params.filter) {
      httpParams = httpParams.set('Filter', params.filter);
    }

    if (params.activityType?.length) {
      params.activityType.forEach((type) => (httpParams = httpParams.append('ActivityType', type.toString())));
    }

    if (params.startDate && params.endDate) {
      httpParams = httpParams.set('StartDate', params.startDate);
      httpParams = httpParams.set('EndDate', params.endDate);
    }

    return this.http.get<PagedResult<UserActivity>>(url, { params: httpParams }).pipe(
      catchError((error) => {
        console.error('Error fetching user activity logs:', error);
        return of({ totalCount: 0, items: [] });
      }),
    );
  }

  /**
   * Export user activity
   * @param payload
   */
  exportActivityLogs(payload: LogsExportRequest): Observable<{ blob: Blob; filename: string } | null> {
    const url = `${this.apiUrl}/logs/export`;

    return this.http
      .post(url, payload, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          const blob = res.body!;
          // Format current date and time
          const now = new Date();
          const date = now.toISOString().slice(0, 10);
          const time = now.toTimeString().slice(0, 8);

          const filename = `ActivityLog-${date}-${time}.${this.getExtensionFromFormat(payload.exportFormat)}`;

          return { blob, filename };
        }),
        catchError((error) => {
          console.error('Export failed:', error);
          return of(null);
        }),
      );
  }

  private getExtensionFromFormat(format: ExportFormatEnum): string {
    switch (format) {
      case ExportFormatEnum.xlsx:
        return 'xlsx';
      case ExportFormatEnum.csv:
        return 'csv';
      case ExportFormatEnum.pdf:
        return 'pdf';
      default:
        return 'bin';
    }
  }
  /**
   * Fallback error handler that returns a default value
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: ${error.message}`);
      // Let the app keep running by returning a default result
      return of(result as T);
    };
  }
}
